import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { Button } from '../ui/Button.jsx';
import { Card } from '../ui/Card.jsx';
import { Spinner } from '../ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { formatDate } from '../../utils/formatters.js';

const EXT_MIME = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  xls: 'application/vnd.ms-excel',
};

function mimeForFile(file) {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return EXT_MIME[ext] || 'application/octet-stream';
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * Generic document-attachments panel, reused across entities (invoices, projects, …).
 * @param {{
 *   queryKey: unknown[],
 *   api: {
 *     list: () => Promise<Array>,
 *     upload: (body: { filename, mimeType, contentBase64 }) => Promise<unknown>,
 *     remove: (attachmentId: string) => Promise<unknown>,
 *     download: (attachmentId: string, filename: string) => void,
 *   },
 * }} props
 */
export function AttachmentsPanel({ queryKey, api }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileRef = useRef(null);

  const list = useQuery({ queryKey, queryFn: api.list });
  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const upload = useMutation({
    mutationFn: async (file) => {
      const contentBase64 = await readBase64(file);
      return api.upload({ filename: file.name, mimeType: mimeForFile(file), contentBase64 });
    },
    onSuccess: () => {
      invalidate();
      if (fileRef.current) fileRef.current.value = '';
    },
  });

  const remove = useMutation({ mutationFn: (id) => api.remove(id), onSuccess: invalidate });

  const rows = list.data || [];

  return (
    <Card
      title={t('attachments.title')}
      actions={
        <>
          <input
            ref={fileRef}
            type="file"
            style={{ display: 'none' }}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
            }}
          />
          <Button variant="primary" size="sm" disabled={upload.isPending} onClick={() => fileRef.current?.click()}>
            {upload.isPending ? `${t('attachments.upload')}…` : t('attachments.upload')}
          </Button>
        </>
      }
    >
      <p className="muted text-sm" style={{ marginTop: 0 }}>{t('attachments.hint')}</p>

      {upload.isError && (
        <div className="alert danger">{upload.error?.message || t('common.loadError')}</div>
      )}

      {list.isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="muted text-sm">{t('attachments.empty')}</p>
      ) : (
        <div className="attachment-list">
          {rows.map((a) => (
            <div key={a.id} className="attachment-row">
              <div className="attachment-info">
                <button type="button" className="attachment-name" onClick={() => api.download(a.id, a.filename)}>
                  {a.filename}
                </button>
                <span className="muted text-xs">
                  {formatSize(a.sizeBytes)} · {formatDate(a.createdAt)}
                </span>
              </div>
              <Button size="sm" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(a.id)}>
                {t('attachments.delete')}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
